import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { newsList } from "@/mocks/news";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import NewsCard from "@/components/feature/NewsCard";
import EmptyState from "@/components/base/EmptyState";

const categories = ["全部", "新片动态", "追剧指南", "行业观察", "影展现场", "深度专题"];

export default function NewsPage() {
  useDocumentMeta(
    "日韩影视资讯 - 新片动态与行业观察 | 日韩影视大全",
    "整理日韩影视最新资讯：新片定档、影展现场、日剧韩剧追剧指南与行业观察，帮助你快速了解日韩影视动态。",
    "日韩影视资讯,日韩电影资讯,日韩电视剧资讯,日影,韩影",
  );

  const [active, setActive] = useState("全部");
  const filtered = useMemo(
    () =>
      active === "全部"
        ? newsList.slice(1)
        : newsList.filter((item) => item.category === active),
    [active],
  );
  const featured = newsList[0];

  return (
    <div className="w-full pb-16">
      <section className="w-full border-b border-background-200/80 bg-background-100">
        <div className="mx-auto w-full max-w-[1440px] px-4 py-9 md:px-8 md:py-12">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="cursor-pointer transition-colors hover:text-primary-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
            <span className="text-foreground-900">影视资讯</span>
          </nav>
          <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <p className="flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.22em] text-foreground-500">
                <span className="h-px w-6 bg-accent-500" />
                Editorial
              </p>
              <h1 className="mt-2 font-heading text-2xl font-semibold text-foreground-950 md:text-4xl">
                日韩影视资讯
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                新片定档、影展现场、追剧指南与行业观察。用专题报道的方式，记录日韩影视正在发生的事。
              </p>
            </div>
            <div className="flex items-center gap-3 text-xs text-foreground-500">
              <span className="rounded-full border border-background-300 bg-background-50 px-3 py-1.5">
                共 {newsList.length} 篇专题
              </span>
              <Link
                to="/search"
                className="cursor-pointer rounded-full border border-background-300 bg-background-50 px-3 py-1.5 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                搜索影视作品
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8">
        <Link
          to={`/news/${featured.id}`}
          className="group mt-6 grid grid-cols-1 overflow-hidden rounded-lg border border-background-200/80 bg-background-50 lg:grid-cols-[1.15fr_1fr]"
        >
          <div className="relative aspect-[16/9] w-full overflow-hidden bg-background-200 lg:aspect-auto lg:h-full">
            <img
              src={featured.cover}
              alt={`${featured.title} 配图`}
              title={`${featured.title} - 日韩影视资讯`}
              className="h-full w-full object-cover object-top transition-transform duration-700 ease-smooth group-hover:scale-[1.03]"
            />
            <span className="absolute left-4 top-4 rounded-full bg-accent-500 px-2.5 py-1 text-[11px] font-semibold text-primary-950">
              本期头条
            </span>
          </div>
          <div className="flex flex-col justify-center p-5 md:p-8">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-foreground-500">
              <span className="rounded-sm bg-secondary-100 px-1.5 py-0.5 text-secondary-800">
                {featured.category}
              </span>
              <time dateTime={featured.date}>{featured.date.replace(/-/g, ".")}</time>
              <span className="text-background-400">·</span>
              <span>{featured.readTime}</span>
            </div>
            <h2 className="mt-3 font-heading text-xl font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-2xl">
              {featured.title}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-500">{featured.excerpt}</p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-primary-700">
              阅读专题
              <i className="ri-arrow-right-line text-sm" />
            </span>
          </div>
        </Link>

        <div className="mt-6 flex flex-col gap-3 border-b border-background-200/80 pb-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-1.5">
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => setActive(category)}
                className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-xs transition-colors ${
                  active === category
                    ? "bg-primary-950 font-medium text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
          <span className="text-[11px] text-foreground-500">
            {active === "全部" ? "全部资讯" : active} · {filtered.length} 篇
          </span>
        </div>

        <div className="mt-6">
          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 gap-x-5 gap-y-9 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <EmptyState
              title="这个分类暂时没有资讯"
              description="换个分类看看，或者回到首页浏览最新影视作品。"
            >
              <button
                type="button"
                onClick={() => setActive("全部")}
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-950 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800"
              >
                查看全部资讯
              </button>
            </EmptyState>
          )}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-background-100 px-5 py-4">
          <p className="text-xs text-foreground-500">想先找作品？可以直接搜索片名、主演或导演。</p>
          <Link
            to="/search"
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-xs font-medium text-primary-700 transition-colors hover:text-primary-600"
          >
            前往搜索页
            <i className="ri-arrow-right-line text-sm" />
          </Link>
        </div>
      </div>
    </div>
  );
}